import { removeTrailingSlash } from 'utils'

export interface ResolveShortUrlsOptions {
    /** Extra headers to send (e.g. auth). */
    headers?: Record<string, string>
    /** Fetch credentials mode. Defaults to `'include'`. */
    credentials?: RequestCredentials
}

/**
 * Single implementation of the short-url batch resolve used across apps
 * (chatbot api client, frontend app root). Maps `/api/r/<hash>` short urls back
 * to their real target via `POST <backend>/api/r/resolve`.
 *
 * The redirect endpoint lives at `/api/r`, outside the JSON:API basePath, and
 * is unauthenticated. Returns an empty map (short urls left untouched) on any
 * failure so callers never have to handle errors.
 */
export async function resolveShortUrls(
    backendUrl: string,
    hashes: string[],
    options: ResolveShortUrlsOptions = {},
): Promise<Record<string, string>> {
    try {
        const root = removeTrailingSlash(backendUrl)
        const response = await fetch(`${root}/api/r/resolve`, {
            method: 'POST',
            credentials: options.credentials ?? 'include',
            headers: {
                ...options.headers,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ hashes }),
        })
        if (!response.ok) {
            return {}
        }
        const data = (await response.json()) as {
            urls?: Record<string, string>
        }
        return data.urls ?? {}
    } catch {
        return {}
    }
}
